// Keyboard shortcuts, clipboard, paste and drag-and-drop for a board.
import type { CardElement, ImageElement, StickerElement, TextElement } from "../../shared/model";
import type { Editor, ToolId } from "../editor/editor";
import { newId } from "../editor/elements";
import { measureText } from "../editor/measure";
import type { Vec } from "../editor/geometry";
import { stickerInfo } from "../data/stickers";
import { INKS } from "../editor/style";
import type { TextTool } from "../editor/tools/create";

/** Keys go to a text field (the hidden keyboard field too: its letters belong to the text box about to open). */
const isTyping = () => {
  const a = document.activeElement as HTMLElement | null;
  return !!a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA" || a.isContentEditable);
};
/** a key this soon after a text box closed is the end of fast typing, not a shortcut */
const LATE_TYPING_MS = 1500;

/** Downscale and compress a photo in the browser before storing it. */
export async function prepareImage(file: Blob, maxSide = 2200): Promise<{ blob: Blob; w: number; h: number }> {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) throw new Error("That file doesn't look like an image.");
  const k = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * k);
  const h = Math.round(bitmap.height * k);
  if (k === 1 && file.size < 1_500_000 && /image\/(png|jpeg|webp|gif)/.test(file.type)) {
    bitmap.close?.();
    return { blob: file, w, h };
  }
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  const hasAlpha = file.type === "image/png" || file.type === "image/webp" || file.type === "image/gif";
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, hasAlpha ? "image/png" : "image/jpeg", 0.86));
  if (!blob) throw new Error("Couldn't read that image.");
  return { blob, w, h };
}

export async function insertImages(ed: Editor, files: File[], at?: Vec): Promise<void> {
  const images = files.filter((f) => f.type.startsWith("image/"));
  if (!images.length) return;
  const center = ed.placementPoint(at);
  const made: ImageElement[] = [];
  let offset = 0;
  for (const f of images.slice(0, 12)) {
    try {
      const prepared = await prepareImage(f);
      const up = await ed.host.uploadImage(prepared.blob);
      const maxW = 520 / Math.max(0.4, Math.min(1.6, ed.camera.z));
      const k = Math.min(1, maxW / up.w);
      const w = up.w * k;
      const h = up.h * k;
      made.push({
        id: newId(), type: "image", src: up.src, natW: up.w, natH: up.h, x: center.x - w / 2 + offset, y: center.y - h / 2 + offset, w, h,
        rot: 0, z: "", t: Date.now(), frame: "none"
      });
      offset += 30;
    } catch (err) {
      ed.host.toast((err as Error).message || "Couldn't add that image.", "error");
    }
  }
  if (made.length) ed.insert(made);
}

function insertText(ed: Editor, text: string, at?: Vec): void {
  const c = ed.placementPoint(at);
  const trimmed = text.trim();
  if (!trimmed) return;
  const multi = trimmed.includes("\n") || trimmed.length > 90 || /^#|^[-*] |\[\[|\$/.test(trimmed);
  if (multi) {
    const el: CardElement = {
      id: newId(), type: "card", x: c.x - 180, y: c.y - 130, w: 360, h: 260, rot: 0, z: "", t: Date.now(), md: trimmed.slice(0, 20000),
      paper: ed.style.cardPaper, accent: "#c2583a", fontSize: 17, shadow: true
    };
    ed.insert([el]);
    return;
  }
  const st = ed.style;
  const m = measureText(trimmed, st.font, st.fontSize);
  const el: TextElement = {
    id: newId(), type: "text", x: c.x - m.w / 2, y: c.y - m.h / 2, w: m.w, h: m.h, rot: 0, z: "", t: Date.now(), text: trimmed, font: st.font,
    fontSize: st.fontSize, color: st.textColor, align: "left", autoW: true
  };
  ed.insert([el]);
}

const TOOL_KEYS: Record<string, ToolId> = {
  v: "select", l: "lasso", h: "hand", p: "pen", m: "highlighter", e: "eraser", r: "shape", o: "shape", d: "shape", a: "line",
  t: "text", n: "note", c: "card", f: "frame", s: "sticker", k: "laser"
};

export interface IoHandlers {
  onImagePicker: () => void;
  onOpenStickers: () => void;
  onPresent: () => void;
  onHelp: () => void;
  onExport: () => void;
  onNewLayer: () => void;
}

/** Wires keyboard, clipboard and drop handling for as long as the board is open. */
export function attachBoardIo(ed: Editor, h: IoHandlers): () => void {
  const pointerWorld = { current: null as Vec | null };
  const onMoveTrack = (e: PointerEvent) => {
    const vp = ed.viewport;
    if (!vp) return;
    const r = vp.getBoundingClientRect();
    pointerWorld.current = ed.camera.toWorld({ x: e.clientX - r.left, y: e.clientY - r.top });
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.defaultPrevented) return;
    const mod = e.metaKey || e.ctrlKey;
    const k = e.key.toLowerCase();
    if (isTyping()) return;
    if (document.querySelector(".dialog-scrim")) return;

    if (mod && k === "z") {
      e.preventDefault();
      e.shiftKey ? ed.redo() : ed.undo();
      return;
    }
    if (mod && k === "y") {
      e.preventDefault();
      ed.redo();
      return;
    }
    if (mod && k === "a") {
      e.preventDefault();
      ed.selectAll();
      return;
    }
    if (mod && k === "d") {
      e.preventDefault();
      ed.duplicateSelected();
      return;
    }
    if (mod && k === "g") {
      e.preventDefault();
      e.shiftKey ? ed.ungroup() : ed.group();
      return;
    }
    if (mod && e.shiftKey && k === "l") {
      e.preventDefault();
      ed.toggleLock();
      return;
    }
    if (mod && e.shiftKey && k === "n") {
      e.preventDefault();
      h.onNewLayer();
      return;
    }
    if (mod && (k === "=" || k === "+")) {
      e.preventDefault();
      ed.zoomBy(1.25);
      return;
    }
    if (mod && k === "-") {
      e.preventDefault();
      ed.zoomBy(0.8);
      return;
    }
    if (mod && k === "0") {
      e.preventDefault();
      ed.resetZoom();
      return;
    }
    if (mod && k === "e") {
      e.preventDefault();
      h.onExport();
      return;
    }
    if (mod && (k === "]" || k === "[")) {
      e.preventDefault();
      ed.reorder(k === "]" ? (e.shiftKey ? "front" : "forward") : e.shiftKey ? "back" : "backward");
      return;
    }
    if (mod) return;

    // writing with the keyboard: a letter with no text box open starts a new line, never a tool shortcut
    if (ed.keyboardMode && !ed.ui.readOnly && e.key.length === 1 && e.key.trim()) {
      e.preventDefault();
      (ed.tools.get("text") as TextTool).typeKey(e.key);
      return;
    }
    // typing fast past the end of a text box (it closed, or lost the focus) must not switch tools or delete things
    if (performance.now() - ed.editEndedAt < LATE_TYPING_MS && k !== "escape" && k !== "enter") return;

    if (e.shiftKey && (e.code === "Digit1" || k === "!")) {
      ed.zoomToFit();
      return;
    }
    if (e.shiftKey && (e.code === "Digit2" || k === "@")) {
      ed.zoomToSelection();
      return;
    }
    if (k === "escape") {
      if (ed.ui.selection.length) ed.clearSelection();
      else if (ed.ui.tool !== "select" && !ed.keyboardMode) ed.setTool("select");
      return;
    }
    if (k === "delete" || k === "backspace") {
      e.preventDefault();
      ed.deleteSelected();
      return;
    }
    if (k === "enter" && ed.ui.selection.length === 1) {
      e.preventDefault();
      ed.startEditing(ed.ui.selection[0]);
      return;
    }
    if (k.startsWith("arrow") && ed.ui.selection.length) {
      e.preventDefault();
      const step = (e.shiftKey ? 10 : 1) / Math.min(1, ed.camera.z);
      ed.nudge(k === "arrowleft" ? -step : k === "arrowright" ? step : 0, k === "arrowup" ? -step : k === "arrowdown" ? step : 0);
      return;
    }
    if (k === "?") {
      h.onHelp();
      return;
    }
    if (k === "i") {
      h.onImagePicker();
      return;
    }
    if (TOOL_KEYS[k]) {
      const tool = TOOL_KEYS[k];
      if (tool === "shape") ed.setStyle({ shapeKind: k === "o" ? "ellipse" : k === "d" ? "diamond" : "rect" });
      if (tool === "sticker") h.onOpenStickers();
      ed.setTool(tool);
      return;
    }
    if (/^[1-9]$/.test(k)) {
      // number keys pick an ink for the current tool (or the selection)
      const c = INKS[Number(k) - 1]?.color;
      if (!c) return;
      if (ed.ui.selection.length) {
        ed.updateSelected((el) =>
          el.type === "ink" ? { ...el, color: c } : el.type === "shape" || el.type === "line" ? { ...el, stroke: c } : el.type === "text" ? { ...el, color: c } : null
        );
      } else ed.setStyle({ inkColor: c, stroke: c, textColor: c });
    }
  };

  const onCopy = (e: ClipboardEvent) => {
    if (isTyping() || !ed.ui.selection.length) return;
    ed.copy();
    const payload = JSON.stringify({ "sepia/clipboard/v1": true, els: ed.selected() });
    e.clipboardData?.setData("text/plain", payload);
    e.preventDefault();
  };
  const onCut = (e: ClipboardEvent) => {
    if (isTyping() || !ed.ui.selection.length || ed.ui.readOnly) return;
    onCopy(e);
    ed.deleteSelected();
  };
  const onPaste = (e: ClipboardEvent) => {
    if (isTyping() || ed.ui.readOnly) return;
    const dt = e.clipboardData;
    if (!dt) return;
    const files = [...dt.files];
    if (files.length) {
      e.preventDefault();
      insertImages(ed, files, pointerWorld.current ?? undefined);
      return;
    }
    const text = dt.getData("text/plain");
    const els = ed.parseClipboardText(text);
    e.preventDefault();
    if (els) ed.pasteElements(els, pointerWorld.current ?? undefined);
    else if (text) insertText(ed, text, pointerWorld.current ?? undefined);
  };

  const onDragOver = (e: DragEvent) => {
    if (!ed.viewport?.contains(e.target as Node)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };
  const onDrop = (e: DragEvent) => {
    const vp = ed.viewport;
    if (!vp?.contains(e.target as Node) || ed.ui.readOnly) return;
    e.preventDefault();
    const r = vp.getBoundingClientRect();
    const at = ed.placementPoint(ed.camera.toWorld({ x: e.clientX - r.left, y: e.clientY - r.top }));
    const sticker = e.dataTransfer?.getData("application/x-sepia-sticker");
    if (sticker) {
      const info = stickerInfo(sticker);
      if (!info) return;
      const k = 150 / Math.max(info.w, info.h);
      const el: StickerElement = {
        id: newId(), type: "sticker", key: sticker, x: at.x - (info.w * k) / 2, y: at.y - (info.h * k) / 2, w: info.w * k, h: info.h * k,
        rot: ((Math.random() - 0.5) * 8 * Math.PI) / 180, z: "", t: Date.now()
      };
      ed.insert([el]);
      return;
    }
    const files = [...(e.dataTransfer?.files ?? [])];
    if (files.length) insertImages(ed, files, at);
    else {
      const text = e.dataTransfer?.getData("text/plain");
      if (text) insertText(ed, text, at);
    }
  };

  window.addEventListener("keydown", onKey);
  window.addEventListener("copy", onCopy);
  window.addEventListener("cut", onCut);
  window.addEventListener("paste", onPaste);
  window.addEventListener("pointermove", onMoveTrack, { passive: true });
  window.addEventListener("dragover", onDragOver);
  window.addEventListener("drop", onDrop);
  return () => {
    window.removeEventListener("keydown", onKey);
    window.removeEventListener("copy", onCopy);
    window.removeEventListener("cut", onCut);
    window.removeEventListener("paste", onPaste);
    window.removeEventListener("pointermove", onMoveTrack);
    window.removeEventListener("dragover", onDragOver);
    window.removeEventListener("drop", onDrop);
  };
}

export { insertText };
